const baseFields = ['id', 'stationId', 'controllerId', 'eventAt', 'eventTimeLocal', 'eventTimeQuality', 'stationTimeZone', 'receivedAt']
const rowFields = {
  transactions: [...baseFields, 'transactionNumber', 'shiftNumber', 'pumpNumber', 'nozzleNumber', 'tankNumber', 'fuelGradeId', 'fuelGradeName', 'volume', 'price', 'amount'],
  measurements: [...baseFields, 'tankNumber', 'fuelGradeId', 'fuelGradeName', 'status', 'alarms', 'alarmDataState', 'dataComplete', 'displayState', 'productVolume', 'productHeight', 'waterHeight', 'temperature'],
  deliveries: [...baseFields, 'startedAt', 'startTimeLocal', 'startTimeQuality', 'durationSeconds', 'tankNumber', 'fuelGradeId', 'fuelGradeName', 'startProductVolume', 'endProductVolume', 'productVolume', 'pumpsDispensedVolume', 'dataComplete', 'displayState'],
  alerts: [...baseFields, 'timeZoneBasis', 'deviceType', 'deviceNumber', 'deviceQuality', 'eventState', 'code', 'codeQuality', 'catalogVersion', 'catalogName', 'catalogStatus'],
}
const filterFields = {
  transactions: ['from', 'to', 'transactionNumber', 'pumpNumbers', 'nozzleNumbers', 'tankNumbers', 'fuelGradeIds', 'shiftMode', 'shiftNumber', 'tagMode', 'tagRef'],
  measurements: ['from', 'to', 'tankNumbers', 'fuelGradeIds', 'statusMode', 'alarmMode', 'completenessMode'],
  deliveries: ['from', 'to', 'tankNumbers', 'fuelGradeIds', 'completeness'],
  alerts: ['from', 'to', 'deviceMode', 'deviceType', 'deviceNumber', 'stateMode', 'codeMode', 'code'],
}
const summaries = {
  measurements: ['all', 'withAlarms', 'errors', 'incomplete'],
  deliveries: ['all', 'productVolume', 'productVolumeKnown', 'pumpsDispensedVolume', 'pumpsDispensedKnown', 'incomplete'],
}
const clone = (value) => JSON.parse(JSON.stringify(value))
const scalar = (value) => value === null || ['string', 'number', 'boolean'].includes(typeof value)
function pick(value, fields) {
  return Object.fromEntries(fields.filter((key) => Object.hasOwn(value ?? {}, key) &&
    (scalar(value[key]) || Array.isArray(value[key]) && value[key].every(scalar)))
    .map((key) => [key, Array.isArray(value[key]) ? [...value[key]] : value[key]]))
}

export function projectHistoryPage(flow, value) {
  if (!rowFields[flow]) throw new Error('Unknown PTS-2 history flow')
  const result = pick(value, flow === 'alerts' ? ['stationId', 'controllerId', 'page', 'limit', 'total', 'totalPages'] : ['page', 'limit', 'totalItems', 'totalPages'])
  result.items = (value.items ?? []).slice(0, 100).map((item) => pick(item, rowFields[flow]))
  if (summaries[flow]) result.summary = pick(value.summary, summaries[flow])
  return result
}

export function historyFilters(flow, value = {}) {
  const result = pick(value, filterFields[flow] ?? [])
  if (flow === 'transactions') {
    if (typeof value.shiftSelection === 'string') {
      const [mode, number] = value.shiftSelection.split(':'); result.shiftMode = mode
      result.shiftNumber = mode === 'NUMBER' ? Number(number) : null
    }
    if (typeof value.tagSelection === 'string') {
      const [mode, ref] = value.tagSelection.split(':'); result.tagMode = mode
      result.tagRef = mode === 'REF' ? ref : null
    }
  }
  return result
}
export function historyQueryKey(flow, { applied, pageNumber, pageSize }) {
  const filters = historyFilters(flow, applied)
  return JSON.stringify([flow, Object.keys(filters).sort().map((key) => [key,
    Array.isArray(filters[key]) ? [...filters[key]].sort((a, b) => a - b) : filters[key]]), pageNumber, pageSize])
}
export function hasHistoryDraftChanges(flow, draft, applied) {
  return historyQueryKey(flow, { applied: draft }) !== historyQueryKey(flow, { applied })
}
function labels(value = {}) {
  const result = {}
  for (const facet of ['TANK', 'FUEL', 'PUMP', 'NOZZLE']) {
    if (Array.isArray(value[facet])) result[facet] = value[facet].slice(0, 20).map((item) => pick(item, ['number', 'name', 'metadataState']))
  }
  if (value.TAG) result.TAG = { ...pick(value.TAG, ['ref']), tag: pick(value.TAG.tag, ['name', 'nameState', 'maskedValue']) }
  return result
}
export function historyScope(session) {
  if (!session.user) return ''
  const platform = session.platformContext, pts2 = session.pts2Context
  const station = platform?.stations?.find((item) => Number(item.id) === Number(platform.activeStationId))
  return JSON.stringify([session.user.id ?? session.user.username, session.user.accessVersion, session.user.role,
    [...(session.user.effectivePermissions ?? [])].sort(), platform?.activeStationId, pts2?.stationId,
    pts2?.activeControllerId, station?.timezone ?? null])
}

// One last page per flow, memory-only. A ticket fences writes even after switch-away/back.
export function createHistoryState({ now = () => Date.now(), staleTime = 60_000 } = {}) {
  let scope = '', epoch = 0
  const entries = new Map()
  const ticket = () => ({ scope, epoch })
  const current = (token) => Boolean(scope) && token?.scope === scope && token?.epoch === epoch
  const clear = () => { entries.clear(); epoch++ }
  function setScope(value) { if (value !== scope) { clear(); scope = value } }
  function savePreferences(flow, token, value) {
    if (!rowFields[flow] || !current(token)) return false
    const preferences = { applied: historyFilters(flow, value.applied),
      draft: pick(value.draft ?? value.applied, [...filterFields[flow], ...(flow === 'transactions' ? ['shiftSelection', 'tagSelection'] : [])]),
      pageNumber: value.pageNumber, pageSize: value.pageSize, selectedLabels: labels(value.selectedLabels) }
    if (flow === 'transactions') preferences.searchDraft = String(value.searchDraft ?? '')
    entries.set(flow, { ...entries.get(flow), preferences }); return true
  }
  function saveResult(flow, token, query, value) {
    if (!rowFields[flow] || !current(token)) return false
    entries.set(flow, { ...entries.get(flow), result: { key: historyQueryKey(flow, query), page: projectHistoryPage(flow, value), fetchedAt: now() } })
    return true
  }
  function restore(flow, token) {
    if (!current(token)) return null
    const entry = entries.get(flow)
    if (!entry?.preferences) return null
    const matching = entry.result?.key === historyQueryKey(flow, entry.preferences)
    return clone({ ...entry.preferences, page: matching ? entry.result.page : null,
      fetchedAt: matching ? entry.result.fetchedAt : null, fresh: matching && now() - entry.result.fetchedAt < staleTime })
  }
  return { setScope, clear, ticket, current, savePreferences, saveResult, restore }
}
